/**
 * Import intelligence — derive summaries, hints, and cleanup actions from parsed mods.
 * Uses only fields already present on parsed modifications (no parser changes).
 */

import {
  buildReviewHierarchy,
  countDistinctGroups,
  formatSpecifications,
  getGroupLabel,
  groupKey,
  type ParsedModLike,
} from './importReviewGrouping';
import type { ParsedModification } from '../components/import/importReviewTypes';

export type ImportConfidenceLevel = 'high' | 'medium' | 'low';

export interface ImportIntelligenceSummary {
  modificationCount: number;
  categoryCount: number;
  sectionCount: number;
  brandCount: number;
  specsCount: number;
  organized: Array<{
    category: string;
    sections: string[];
    moreSectionCount: number;
  }>;
}

export interface ConfidenceHint {
  tone: 'positive' | 'review';
  label: string;
}

export type ImportReviewFilter =
  | 'all'
  | 'warnings'
  | 'duplicates'
  | 'uncategorized'
  | 'excluded';

export interface CleanupSuggestion {
  id: string;
  message: string;
  actionLabel?: string;
  action?: 'filter-warnings' | 'filter-uncategorized' | 'exclude-duplicates' | 'expand-section';
  meta?: { category?: string; groupKey?: string; groupLabel?: string };
}

const MISC = 'Miscellaneous';
const UNGROUPED = 'General';

const KNOWN_CATEGORY_SIGNALS = [
  'Engine', 'Exhaust', 'Intake', 'Suspension', 'Brakes', 'Wheels', 'Tires',
  'Interior', 'Exterior', 'Electronics', 'Turbo/Supercharger', 'Fuel System',
  'Transmission', 'Drivetrain', 'Cooling', 'Ignition', 'Maintenance', 'Audio',
  'Performance', 'Cosmetic', 'Safety', 'Lighting',
];

export function hasStructuredSpecs(mod: ParsedModLike): boolean {
  const text = formatSpecifications(mod.specifications);
  return text.length > 0;
}

export function isKnownCategory(category: string, availableCategories: string[]): boolean {
  const norm = category.trim();
  if (!norm || norm === MISC) return false;
  if (availableCategories.some((c) => c.toLowerCase() === norm.toLowerCase())) return true;
  return KNOWN_CATEGORY_SIGNALS.some((c) => c.toLowerCase() === norm.toLowerCase());
}

export function isKnownBrand(vendor: string | undefined, availableVendors: string[]): boolean {
  if (!vendor?.trim()) return false;
  const v = vendor.trim().toLowerCase();
  return availableVendors.some((b) => b.toLowerCase() === v);
}

export function getImportConfidenceLevel(
  mod: ParsedModification,
  availableCategories: string[],
  availableVendors: string[]
): ImportConfidenceLevel {
  const categoryKnown = isKnownCategory(mod.category, availableCategories);
  const hasGroup = Boolean((mod.group_label || mod.parentComponent || '').trim());
  const hasBrand = Boolean(mod.vendor?.trim());
  const brandKnown = isKnownBrand(mod.vendor, availableVendors);
  const hasSpecs = hasStructuredSpecs(mod);
  const fromSection = hasGroup && categoryKnown;
  const categoryInferred =
    mod.category !== MISC &&
    !fromSection &&
    (mod.category_match_confidence ?? mod.confidence) < 0.75;

  if (
    mod.category === MISC ||
    mod.confidence < 0.55 ||
    (mod.category_is_new && !mod.category_confirmed)
  ) {
    return 'low';
  }

  if (
    fromSection ||
    (categoryKnown && (brandKnown || hasSpecs)) ||
    (categoryKnown && mod.confidence >= 0.8)
  ) {
    return 'high';
  }

  if (categoryInferred || (hasBrand && !brandKnown) || mod.confidence < 0.75) {
    return 'medium';
  }

  return categoryKnown ? 'high' : 'medium';
}

export function getItemConfidenceHints(
  mod: ParsedModification,
  availableCategories: string[],
  availableVendors: string[]
): ConfidenceHint[] {
  const hints: ConfidenceHint[] = [];
  const group = (mod.group_label || mod.parentComponent || '').trim();
  const categoryKnown = isKnownCategory(mod.category, availableCategories);
  const confidenceLevel = getImportConfidenceLevel(mod, availableCategories, availableVendors);

  if (group) {
    hints.push({ tone: 'positive', label: `Organized under “${group}”` });
  } else if (categoryKnown && mod.category !== MISC) {
    hints.push({ tone: 'positive', label: `Organized in ${mod.category}` });
  }

  if (mod.vendor?.trim()) {
    if (isKnownBrand(mod.vendor, availableVendors)) {
      hints.push({ tone: 'positive', label: `Detected brand: ${mod.vendor.trim()}` });
    } else {
      hints.push({ tone: 'positive', label: `Brand: ${mod.vendor.trim()}` });
    }
  }

  if (hasStructuredSpecs(mod)) {
    hints.push({ tone: 'positive', label: 'Specifications found' });
  }

  const needsCategoryReview =
    mod.category === MISC ||
    (mod.category_is_new && !mod.category_confirmed) ||
    confidenceLevel === 'low';

  if (needsCategoryReview) {
    hints.push({ tone: 'review', label: 'Needs a look' });
  } else if (confidenceLevel === 'medium' && mod.category !== MISC) {
    hints.push({ tone: 'review', label: 'Needs a look' });
  }

  return hints.slice(0, 3);
}

export function buildImportIntelligenceSummary(
  mods: ParsedModification[]
): ImportIntelligenceSummary {
  const hierarchy = buildReviewHierarchy(mods);
  const brands = new Set(
    mods.map((m) => m.vendor?.trim()).filter(Boolean) as string[]
  );
  const specsCount = mods.filter(hasStructuredSpecs).length;

  const organized = hierarchy.map(({ category, groups }) => {
    const sections = groups
      .map((g) => g.label)
      .filter((label) => label !== UNGROUPED);
    const visible = sections.slice(0, 6);
    return {
      category,
      sections: visible,
      moreSectionCount: Math.max(0, sections.length - visible.length),
    };
  });

  return {
    modificationCount: mods.length,
    categoryCount: hierarchy.length,
    sectionCount: countDistinctGroups(mods),
    brandCount: brands.size,
    specsCount,
    organized,
  };
}

export function itemNeedsReview(
  mod: ParsedModification,
  availableCategories: string[],
  availableVendors: string[]
): boolean {
  if (mod.duplicate_warning && mod.duplicate_resolution !== 'separate') return true;
  if (mod.category === MISC) return true;
  if (mod.category_is_new && !mod.category_confirmed) return true;
  return getImportConfidenceLevel(mod, availableCategories, availableVendors) === 'low';
}

export function itemMatchesFilter(
  mod: ParsedModification,
  index: number,
  filter: ImportReviewFilter,
  availableCategories: string[],
  availableVendors: string[],
  excludedIndices: Set<number>
): boolean {
  const excluded = excludedIndices.has(index);

  if (filter === 'excluded') {
    return excluded;
  }

  if (excluded) {
    // Still show excluded items when filter is all; hide from focused filters
    if (filter === 'all') return true;
    return false;
  }

  switch (filter) {
    case 'warnings':
      return itemNeedsReview(mod, availableCategories, availableVendors);
    case 'duplicates':
      return Boolean(mod.duplicate_warning && mod.duplicate_resolution !== 'separate');
    case 'uncategorized':
      return mod.category === MISC;
    default:
      return true;
  }
}

export function itemMatchesSearch(mod: ParsedModification, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const haystack = [
    mod.title,
    mod.vendor,
    mod.category,
    mod.group_label,
    mod.parentComponent,
    mod.notes,
    formatSpecifications(mod.specifications),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return haystack.includes(q);
}

export function getVisibleModificationIndices(
  mods: ParsedModification[],
  filter: ImportReviewFilter,
  searchQuery: string,
  excludedIndices: Set<number>,
  availableCategories: string[],
  availableVendors: string[]
): Set<number> | null {
  const hasFilter = filter !== 'all' || searchQuery.trim().length > 0;
  if (!hasFilter) return null;

  const visible = new Set<number>();
  mods.forEach((mod, index) => {
    if (
      itemMatchesFilter(mod, index, filter, availableCategories, availableVendors, excludedIndices) &&
      itemMatchesSearch(mod, searchQuery)
    ) {
      visible.add(index);
    }
  });
  return visible;
}

export function findSimilarCategoryCandidates(
  mods: ParsedModification[],
  changedIndex: number,
  oldCategory: string,
  newCategory: string
): number[] {
  if (!oldCategory || oldCategory === newCategory) return [];

  const source = mods[changedIndex];
  if (!source) return [];

  const sourceGroup = getGroupLabel(source);
  const sourceVendor = (source.vendor || '').trim().toLowerCase();
  const sourceTitleToken = source.title.trim().split(/\s+/)[0]?.toLowerCase() || '';

  const candidates: Array<{ index: number; score: number }> = [];

  mods.forEach((mod, index) => {
    if (index === changedIndex) return;
    if (mod.category !== oldCategory) return;

    let score = 0;
    if (sourceGroup !== UNGROUPED && getGroupLabel(mod) === sourceGroup) score += 3;
    if (sourceVendor && (mod.vendor || '').trim().toLowerCase() === sourceVendor) score += 2;
    const token = mod.title.trim().split(/\s+/)[0]?.toLowerCase() || '';
    if (sourceTitleToken.length >= 3 && token === sourceTitleToken) score += 1;

    if (score >= 2 || (sourceGroup !== UNGROUPED && getGroupLabel(mod) === sourceGroup)) {
      candidates.push({ index, score });
    }
  });

  return candidates
    .sort((a, b) => b.score - a.score)
    .map((c) => c.index);
}

export function buildCleanupSuggestions(
  mods: ParsedModification[],
  excludedIndices: Set<number>,
  availableCategories: string[],
  availableVendors: string[]
): CleanupSuggestion[] {
  const suggestions: CleanupSuggestion[] = [];
  const included = mods
    .map((m, index) => ({ m, index }))
    .filter(({ index }) => !excludedIndices.has(index));

  const needsCategory = included.filter(
    ({ m }) =>
      m.category === MISC ||
      (m.category_is_new && !m.category_confirmed) ||
      getImportConfidenceLevel(m, availableCategories, availableVendors) === 'low'
  ).length;

  if (needsCategory > 0) {
    suggestions.push({
      id: 'review-categories',
      message: `${needsCategory} part${needsCategory === 1 ? '' : 's'} may need categories reviewed`,
      actionLabel: 'Show them',
      action: 'filter-warnings',
    });
  }

  const dupes = included.filter(({ m }) => m.duplicate_warning && m.duplicate_resolution !== 'separate').length;
  if (dupes > 0) {
    suggestions.push({
      id: 'duplicates',
      message: `${dupes} possible duplicate${dupes === 1 ? '' : 's'} detected`,
      actionLabel: 'Review duplicates',
      action: 'filter-warnings',
    });
  }

  // Prefer real section labels (group_label / parentComponent). The recursive review
  // tree uses a synthetic "Tree" group that is not a scroll/collapse target.
  const sectionCounts = new Map<string, { category: string; label: string; count: number }>();
  included.forEach(({ m }) => {
    const category = (m.category || MISC).trim() || MISC;
    const label = getGroupLabel(m);
    if (label === UNGROUPED) return;
    const key = groupKey(category, label);
    const prev = sectionCounts.get(key);
    if (prev) prev.count += 1;
    else sectionCounts.set(key, { category, label, count: 1 });
  });

  const sectionSuggestions = Array.from(sectionCounts.values())
    .filter((s) => s.count >= 3)
    .sort((a, b) => b.count - a.count);

  if (sectionSuggestions.length > 0) {
    sectionSuggestions.slice(0, 3).forEach((section) => {
      suggestions.push({
        id: `group-${groupKey(section.category, section.label)}`,
        message: `${section.count} parts grouped under ${section.category} / ${section.label}`,
        actionLabel: 'Jump to section',
        action: 'expand-section',
        meta: {
          category: section.category,
          groupKey: groupKey(section.category, section.label),
          groupLabel: section.label,
        },
      });
    });
  } else {
    // Fall back to category jumps when the list is a recursive tree without section labels.
    const categoryCounts = new Map<string, number>();
    included.forEach(({ m }) => {
      const category = (m.category || MISC).trim() || MISC;
      categoryCounts.set(category, (categoryCounts.get(category) || 0) + 1);
    });
    Array.from(categoryCounts.entries())
      .filter(([, count]) => count >= 5)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .forEach(([category, count]) => {
        suggestions.push({
          id: `category-${category}`,
          message: `${count} parts in ${category}`,
          actionLabel: 'Jump to section',
          action: 'expand-section',
          meta: { category },
        });
      });
  }

  const miscOnly = included.filter(({ m }) => m.category === MISC).length;
  if (miscOnly > 0 && miscOnly !== needsCategory) {
    suggestions.push({
      id: 'uncategorized',
      message: `${miscOnly} part${miscOnly === 1 ? '' : 's'} still in Miscellaneous`,
      actionLabel: 'Show uncategorized',
      action: 'filter-uncategorized',
    });
  }

  return suggestions.slice(0, 5);
}

export function allCategoryKeys(hierarchy: ReturnType<typeof buildReviewHierarchy>): string[] {
  return hierarchy.map((c) => c.category);
}

export function allGroupKeys(hierarchy: ReturnType<typeof buildReviewHierarchy>): string[] {
  return hierarchy.flatMap(({ category, groups }) =>
    groups.map((g) => groupKey(category, g.label))
  );
}

export function importReviewCategoryDomId(category: string): string {
  return `import-review-cat-${encodeURIComponent(category)}`;
}

export function importReviewGroupDomId(groupKeyValue: string): string {
  return `import-review-group-${encodeURIComponent(groupKeyValue)}`;
}

export function importReviewFiltersDomId(): string {
  return 'import-review-filters';
}
